<template>
  <!-- Main Content -->
  <div>
    <!-- Breadcrumb -->
    <div class="page-header">
      <div class="page-block">
        <div class="row align-items-center">
          <div class="col-md-12">
            <div class="page-header-title">
              <h5>{{ $t('purchaseOrder.title') || 'Sales Order' }}</h5>
            </div>
            <ul class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/">{{ $t('common.navigation.home') }}</router-link>
              </li>
              <li class="breadcrumb-item active">{{ $t('purchaseOrder.listTitle') || 'List' }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <ul class="nav nav-tabs invoice-tab border-bottom-0" id="myTab" role="tablist">
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ 'active': activeTab === 'all' }"
                    @click="setActiveTab('all')"
                    type="button"
                    role="tab"
                  >
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('purchaseOrder.tabs.all') || 'All' }} 
                      <span class="po-status-badge rounded-circle bg-light-primary">{{ totalStatusCount }}</span>
                    </span>
                  </button>
                </li>
                <li v-for="s in poStatuses" :key="s.key" class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ 'active': activeTab === s.key }"
                    @click="setActiveTab(s.key)"
                    type="button"
                    role="tab"
                  >
                    <span class="d-flex align-items-center gap-2">
                      {{ s.label }}
                      <span
                        class="po-status-badge rounded-circle"
                        :class="s.badgeClass"
                      >
                        {{ statusCounts[s.key] || 0 }}
                      </span>
                    </span>
                  </button>
                </li>
              </ul>
            </div>
            
            <div class="tab-content" id="myTabContent">
              <!-- All Purchase Orders Tab -->
              <div 
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === 'all' }"
                role="tabpanel"
                tabindex="0"
              >
                <DataGrid
                  :data="allPurchaseOrders"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="pageSize"
                  v-model:searchText="searchText"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="$t('purchaseOrder.errors.itemsFetchFailed') || 'No sales orders found.'"
                  :search-placeholder="$t('purchaseOrder.placeholders.search') || 'Search Sales Order...'"
                  :total-items="totalItems"
                  :storage-key="'purchaseOrderList_all_columnWidths'"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @refresh="fetchPurchaseOrders"
                  @view="viewPurchaseOrder"
                  @edit="editPurchaseOrder"
                  @delete="deletePurchaseOrder"
                  :renderActions="getRenderActions"
                />
              </div>

              <!-- Status-specific tabs -->
              <div
                v-for="status in poStatuses"
                :key="status.key"
                class="tab-pane fade"
                :class="{ 'show active': activeTab === status.key }"
                role="tabpanel"
                tabindex="0"
              >
                <DataGrid
                  :data="getFilteredOrdersByStatus(status.key)"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="pageSize"
                  v-model:searchText="searchText"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="`No ${status.label} Sales Order found.`"
                  :search-placeholder="$t('purchaseOrder.placeholders.search') || 'Search Sales Order...'"
                  :total-items="totalItems"
                  :storage-key="`purchaseOrderList_${status.key}_columnWidths`"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @refresh="fetchPurchaseOrders"
                  @view="viewPurchaseOrder"
                  @edit="editPurchaseOrder"
                  @delete="deletePurchaseOrder"
                  :renderActions="getRenderActions"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useDateFormatter } from '@/composables/useDateFormatter'
import { useNumberFormatter } from '@/composables/useNumberFormatter'
import { usePurchaseOrderPermissions } from '@/composables/usePurchaseOrderPermissions'
import purchaseOrderService from '@/services/purchaseOrderService'
import Swal from 'sweetalert2'
import DataGrid from '@/components/DataGrid.vue'

// Composables
const router = useRouter()
const store = useStore()
const { t } = useI18n()
const { formatDate, formatTimeAgo } = useDateFormatter()
const { formatCurrency } = useNumberFormatter()
const { canEditPurchaseOrders, canDeletePurchaseOrders } = usePurchaseOrderPermissions()

// Reactive data
const isLoading = ref(false)
const error = ref(null)
const purchaseOrders = ref([])
const activeTab = ref('all')
const currentPage = ref(1)
const pageSize = ref(10)
const totalItems = ref(0)
const searchText = ref('')
const statusCounts = ref({})

// Static status tabs matching API statusCounts keys
const poStatuses = [
  { key: 'OrderReceived',    label: t('purchaseOrder.tabs.orderReceived'),    badgeClass: 'bg-light-primary' },
  { key: 'InPreparation',    label: t('purchaseOrder.tabs.inPreparation'),    badgeClass: 'bg-light-warning' },
  { key: 'ReadyForDispatch', label: t('purchaseOrder.tabs.readyForDispatch'), badgeClass: 'bg-light-info' },
  { key: 'SentToVessel',     label: t('purchaseOrder.tabs.sentToVessel'),     badgeClass: 'bg-light-purple' },
  { key: 'DeliveredToPort',  label: t('purchaseOrder.tabs.deliveredToPort'),  badgeClass: 'bg-light-success' },
  { key: 'DeliveredOnboard', label: t('purchaseOrder.tabs.deliveredOnboard'), badgeClass: 'bg-light-success' },
  { key: 'DeletedInNozzle',  label: t('purchaseOrder.tabs.deletedInNozzle'),  badgeClass: 'bg-light-secondary' },
]

// Map trackingStatusDisplay key → translated label
const statusLabelMap = Object.fromEntries(poStatuses.map(s => [s.key, s.label]))

// badgeMap keyed by PurchaseOrderTrackingStatusEnum numeric value (1-7)
const statusBadgeMap = {
  1: 'bg-light-primary',   // OrderReceived
  2: 'bg-light-warning',   // InPreparation
  3: 'bg-light-info',      // ReadyForDispatch
  4: 'bg-light-purple',    // SentToVessel
  5: 'bg-light-success',   // DeliveredToPort
  6: 'bg-light-success',   // DeliveredOnboard
  7: 'bg-light-secondary', // DeletedInNozzle
}

// Computed: Total count for "All" tab (sum of all status counts)
const totalStatusCount = computed(() => {
  let total = 0
  for (const key in statusCounts.value) {
    if (Object.hasOwn(statusCounts.value, key)) {
      total += statusCounts.value[key] || 0
    }
  }
  return total
})

// Define columns for DataGrid (aligned with RFQ list ordering)
const columns = [
  {
    key: 'buyer',
    label: t('purchaseOrder.table.buyer') || 'Buyer',
    width: '18%',
    type: 'truncate',
    sortable: true,
    sortKey: 'buyerTenantName'
  },
  { 
    key: 'poNumber', 
    label: t('purchaseOrder.table.poNumber') || ' Sales Number', 
    width: '22%',
    type: 'truncate'
  },
  { 
    key: 'date', 
    label: t('purchaseOrder.table.date') || 'Date', 
    width: '10%'
  },
  { 
    key: 'created', 
    label: t('purchaseOrder.table.created') || 'Created', 
    width: '12%'
  },
  { 
    key: 'total', 
    label: t('purchaseOrder.table.total') || 'Total', 
    width: '10%'
  },
  {
    key: 'itemCount',
    label: 'Items',
    width: '8%',
    type: 'truncate'
  },
  {
    key: 'status',
    label: t('purchaseOrder.table.status') || 'Status',
    width: '10%',
    type: 'badge',
    badgeMap: statusBadgeMap
  }
]

// Transform purchase orders for DataGrid
const transformPurchaseOrders = (orders) => {
  return orders.map(order => ({
    id: order.id,
    buyer: order.buyerTenantName || '-',
    poNumber: order.purchaseOrderNo || '-',
    poNumberSubtitle: order.title || order.description || '',
    supplier: order.clientTenantName || '-',
    supplierSubtitle: order.supplierCode || '',
    date: formatDate(order.purchaseOrderDate),
    created: order.creationTime ? formatTimeAgo(order.creationTime) : '-',
    total: formatCurrencyWithFallback(order.grandPrice, order.currencyCode),
    itemCount: order.numberOfItems ?? order.itemCount ?? 0,
    status: order.trackingStatus ?? null,
    statusLabel: statusLabelMap[order.trackingStatusDisplay] || order.trackingStatusDisplay || '-',
    trackingStatus: order.trackingStatus,
    trackingStatusDisplay: order.trackingStatusDisplay,
    orderStatus: order.status,
    originalOrder: order // Keep reference to original order for actions
  }))
}

const allPurchaseOrders = computed(() => {
  return transformPurchaseOrders(purchaseOrders.value)
})

const getFilteredOrdersByStatus = () => {
  return transformPurchaseOrders(purchaseOrders.value)
}

// Methods
const setActiveTab = (tab) => {
  activeTab.value = tab
  currentPage.value = 1
  // fetchPurchaseOrders() is called by watch(activeTab)
}

const handleSearch = () => {
  currentPage.value = 1
  fetchPurchaseOrders()
}

const handlePageSizeChange = () => {
  currentPage.value = 1
  fetchPurchaseOrders()
}

const handlePageChange = () => {
  fetchPurchaseOrders()
}



const formatCurrencyWithFallback = (amount, currencyCode) => {
  return formatCurrency(amount, currencyCode || undefined)
}

const fetchPurchaseOrders = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    const searchParams = {
      // DataGrid currentPage is 1-based; serviceFactory converts to API 0-based once.
      page: currentPage.value,
      pageSize: pageSize.value,
      searchText: searchText.value || '',
      sorting: 'creationTime desc'
    }
    
    // Apply server-side filtering by tracking status display when a specific tab is active
    if (activeTab.value && activeTab.value !== 'all') {
      searchParams.trackingStatusDisplay = activeTab.value
    }
    
    const response = await purchaseOrderService.searchPurchaseOrders(searchParams)

    purchaseOrders.value = response?.items || []
    totalItems.value = response?.totalCount || response?.rowCount || 0

    if (typeof response?.currentPage === 'number') {
      currentPage.value = response.currentPage + 1
    }

    if (response?.statusCounts) {
      statusCounts.value = { ...response.statusCounts }
    }
  } catch (err) {
    console.error('Error fetching Sales Order:', err)
    purchaseOrders.value = []
    totalItems.value = 0
  } finally {
    isLoading.value = false
  }
}

const viewPurchaseOrder = (item) => {
  router.push(`/purchase-order/${item.originalOrder?.id || item.id}`)
}

const editPurchaseOrder = (item) => {
  if (item.trackingStatusDisplay === 'DeletedInNozzle') return
  router.push(`/purchase-order/${item.originalOrder?.id || item.id}?edit=true`)
}

const deletePurchaseOrder = async (item) => {
  const order = item.originalOrder || item
  try {
    const result = await Swal.fire({
      title: t('common.confirmations.delete.title', 'Are you sure?'),
      text: t('purchaseOrder.confirmations.delete.message', 'This purchase order will be permanently deleted.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('common.confirmations.delete.confirm', 'Yes, delete it!'),
      cancelButtonText: t('common.confirmations.delete.cancel', 'Cancel')
    })
    
    if (result.isConfirmed) {
      await purchaseOrderService.deletePurchaseOrder(order.id)
      
      store.dispatch('notify', {
        message: t('purchaseOrder.notifications.deleteSuccess', 'Purchase order deleted successfully'),
        type: 'success'
      })
      
      fetchPurchaseOrders()
    }
  } catch (err) {
    console.error('Error deleting purchase order:', err)
    store.dispatch('notify', {
      message: t('purchaseOrder.notifications.deleteFailed', 'Failed to delete purchase order'),
      type: 'error'
    })
  }
}

const getRenderActions = (item) => {
  const order = item.originalOrder || item
  const isDeleted = item.trackingStatus === 7 || order.trackingStatus === 7
  if (isDeleted) return ['view']
  const actions = ['view']
  if (canEditPurchaseOrders() && order.orderStatus === 'Pending') actions.push('edit')
  if (canDeletePurchaseOrders() && order.orderStatus === 'Pending') actions.push('delete')
  return actions
}

// Watchers
watch(activeTab, () => {
  currentPage.value = 1
  fetchPurchaseOrders()
})

// Lifecycle
onMounted(() => {
  fetchPurchaseOrders()
})
</script>

<style scoped>
.invoice-tab .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  color: #6c757d;
  font-weight: 500;
  padding: 12px 20px;
}

.invoice-tab .nav-link.active {
  border-bottom-color: var(--bs-primary);
  color: var(--bs-primary);
  background-color: transparent;
}

.invoice-tab .nav-link:hover {
  color: var(--bs-primary);
}

.po-status-badge {
  width: 24px;
  height: 24px;
  min-width: 24px;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 11px !important;
  font-weight: 600;
  line-height: 1 !important;
  text-align: center;
  padding: 0 !important;
  vertical-align: middle;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>